import axios from 'axios';
import { IHeartbeatDateRangeRequest } from '../models/Heartbeat';
import { IDeviceInfoDateRangeRequest } from '../models/DeviceInformationResponse';
import { IControlSettingsDateRangeRequest } from '../models/ControlSettingsResponse';
import { IPassageDateRangeRequest } from '../models/Passage';
import {
  CancelDownloadCommandsRequest,
  CancelDownloadCommandsResponse,
} from '../models/DownloadSession';
import { ISimDataUsage } from '../models/SimDataUsage';
import store from '../store';

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_ROOT,
  withCredentials: false, // This is the default
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

let token = '';

export default {
  // Web api service call used to get a DeviceUid data contract response given a Passage.Aid input parameter value.
  async getDeviceUid(passageId: number) {
    token = store.getters.token;

    return await apiClient.get(
      '/apexagm/GetDeviceUidForPassageId/?passageId=' + passageId,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  },

  getHeartbeatDateRange(request: IHeartbeatDateRangeRequest) {
    token = store.getters.token;
    return apiClient.post('/apexagm/heartbeats/by-date-range', request, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  getDeviceInfoDateRange(request: IDeviceInfoDateRangeRequest) {
    token = store.getters.token;
    return apiClient.post('/apexagm/device-info/by-date-range', request, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  getControlSettingsDateRange(request: IControlSettingsDateRangeRequest) {
    token = store.getters.token;
    return apiClient.post('/apexagm/control-settings/by-date-range', request, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  getPassageDateRange(request: IPassageDateRangeRequest) {
    token = store.getters.token;
    return apiClient.post('/apexagm/passages/by-date-range', request, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  // EN-11: pig runs (not archived / not on a deleted section) that this AGM
  // participates in, for the Historic Data pig run filter.
  getPigRunsForDevice(uid: number) {
    token = store.getters.token;
    return apiClient.get('/apexagm/' + uid + '/pigruns', {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  getDownloadSessionPassages(downloadSessionId: number) {
    token = store.getters.token;
    return apiClient.get(
      '/download-sessions/' + downloadSessionId + '/passages',
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
  },
  // EN-1506: request timed-out download sessions again (one call for all).
  retryDownloadSessions(downloadSessionIds: number[]) {
    token = store.getters.token;
    return apiClient.post(
      '/download-sessions/retry',
      { downloadSessionIds },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  },
  // EN-1506: which timed-out download sessions a retry can restart (read-only).
  checkDownloadSessionsRetryable(downloadSessionIds: number[]) {
    token = store.getters.token;
    return apiClient.post(
      '/download-sessions/retryable',
      { downloadSessionIds },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  },
  // EN-1515: cancel queued download commands (by command id and/or download
  // session id; at most 100 ids in total). Response: CancelDownloadCommandsResponse.
  cancelDownloadCommands(
    commandIds: number[],
    downloadSessionIds: number[] = []
  ) {
    token = store.getters.token;
    const body: CancelDownloadCommandsRequest = {
      commandIds,
      downloadSessionIds,
    };
    return apiClient.post<CancelDownloadCommandsResponse>(
      '/download-commands/cancel',
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  },
  getDevices() {
    token = store.getters.token;
    return apiClient.get('/apexagm', {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  getDevice(uid: number) {
    token = store.getters.token;
    return apiClient.get('/apexagm/' + uid + '/device', {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  getLatestDeviceInfo(uid: number) {
    token = store.getters.token;
    return apiClient.get('/apexagm/' + uid + '/latestDeviceInfo', {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  // EN-1734: Super SIM (KORE) data usage vs the Fleet data limit. Always 200
  // with state 'unknown' when KORE is unavailable; 404 for another group's AGM.
  getSimDataUsage(uid: number) {
    token = store.getters.token;
    return apiClient.get<ISimDataUsage>('/apexagm/' + uid + '/data-usage', {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  getDeviceHistory(uid: number, type: string) {
    token = store.getters.token;
    return apiClient.get('/apexagm/' + uid + '/' + type, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  getDeviceHistoryForActivePigRun(uid: number | undefined, runId: string) {
    return apiClient.get('/apexagm/' + uid + '/ActivePigRun/' + runId, {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
  },
  getLastPassageData(uid: number) {
    token = store.getters.token;
    return apiClient.get('/apexagm/lastPassage/' + uid, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  getWaveformData(uid: number, waveformId: number) {
    token = store.getters.token;
    return apiClient.get('/apexagm/waveform/' + uid + '/' + waveformId, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  setPassageWaveformVerification(
    passageWaveformId: number,
    isVerified: boolean
  ) {
    token = store.getters.token;
    return apiClient.post(
      '/pigrun/SetPassageWaveformVerification',
      {
        passageWaveformId,
        isVerified,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  },
  EditPassageRunInfo(payload: any) {
    token = store.getters.token;
    return apiClient.post(
      '/apexagm/passage/' +
        payload[0].uid +
        '/' +
        payload[0].passageId +
        '/updateruninfo',
      { newRunId: payload[1], newMarkerName: payload[2] },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  },
  ClearPassageRunInfo(payload: any) {
    token = store.getters.token;
    if (payload.geoId == undefined) {
      payload.geoId = -1;
    }
    if (payload.magId == undefined) {
      payload.magId = -1;
    }
    if (payload.elfId == undefined) {
      payload.elfId = -1;
    }
    if (payload.tmrId == undefined) {
      payload.tmrId = -1;
    }
    if (payload.accId == undefined) {
      payload.accId = -1;
    }
    return apiClient.post(
      '/apexagm/waveform/' +
        payload.uid +
        '/' +
        payload.geoId +
        '/' +
        payload.magId +
        '/' +
        payload.elfId +
        '/' +
        payload.tmrId +
        '/' +
        payload.accId +
        '/clearpipelinesectionmarkerpassage',
      null,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  },
  deleteGpsFix(uid: number) {
    token = store.getters.token;
    return apiClient.delete('/apexagm/' + uid + '/gpsFix', {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  updateTimeStampByPassageId(
    uid: number,
    passageId: number,
    newPassageTime: Date
  ) {
    token = store.getters.token;
    const postBody = { newPassageTime };
    return apiClient.post(
      '/apexagm/passage/' + uid + '/' + passageId + '/updatetimestamp',
      postBody,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  },
};
